import React, { useRef } from "react";
import Drawer from "@material-ui/core/Drawer";
import styled from "styled-components";
import COLORS from "../../../../assets/Colors";
import theme from "../../../../assets/theme";
import {
  Label,
  ButtonContainer,
  Value,
  ProfilePic,
  ProfilePicContainer,
} from "../styles";
import FormField from "../../../common/FormField/FormField";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCTAButton,
} from "../../../common/Buttons";
import useUser from "../../../../hooks/useUser";
import useLoader from "../../../../hooks/useLoader";
import AWSService from "../../../../services/AWSService";
import { Formik } from "formik";
import { EditProfileSchema } from "../../../../helpers/validationSchema";
import { states } from "../../../../helpers/constants";
import AuthService from "../../../../services/AuthService";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../../helpers/functions";
import labels from "../../../../helpers/labels.json";

export default function ProfileModal({ modal, setModal }) {
  const { userObject } = useUser();
  const fileRef = useRef();
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  async function s3Upload(file, field, setFieldValue, setFieldError) {
    if (file.type.includes("image/png", "image/jpeg", "image/jpg")) {
      try {
        setLoader({ state: true, message: `Uploading file: ${file.name} ...` });
        const payload = {
          key: file.name,
        };
        const s3Response = await AWSService.getS3URL(payload);
        if (s3Response.url) {
          const res = await AWSService.uploadToS3(s3Response.url, file);
          setFieldValue(field, res);
          enqueueSnackbar("Successfully Uploaded", {
            variant: "success",
          });
        }
      } catch (error) {
        setFieldError(field, "Could not upload this file");
      } finally {
        setLoader({ state: false });
      }
    } else {
      return enqueueSnackbar("Invalid File Type", {
        variant: "error",
      });
    }
  }

  async function onFormSubmit(values) {
    const postData = JSON.parse(JSON.stringify(values));
    try {
      setLoader({ state: true, message: "Updating profile..." });
      const response = await AuthService.profileUpdate(postData);
      if (response.me) {
        enqueueSnackbar(response.message, {
          variant: "success",
        });
        window.location.reload();
        setModal(false);
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  return (
    <Drawer
      anchor={"right"}
      open={modal}
      onClose={() => setModal(false)}
      disableRestoreFocus={true}
    >
      <DrawerContainer role="presentation">
        <HeadingContainer>
          <Heading>{labels["actions.edit_profile"]}</Heading>
          <CloseModal
            onClick={() => setModal(false)}
            src={require("../../../../assets/images/closeModal.svg")}
          />
        </HeadingContainer>
        <AgentFormContainer>
          <Formik
            initialValues={{
              houseNumber: userObject?.houseNumber || "",
              locality: userObject?.locality || "",
              city: userObject?.city || "",
              state: userObject?.state || "TN",
              pin: userObject?.pin || "",
              landmark: userObject?.landmark || "",
              avatarUrl: userObject?.avatarUrl || "",
              panNumber: userObject?.panNumber || "",
            }}
            enableReinitialize
            validateOnBlur
            validationSchema={EditProfileSchema}
            validateOnChange
            onSubmit={onFormSubmit}
          >
            {({
              values,
              handleChange,
              errors,
              handleSubmit,
              touched,
              setFieldValue,
              setFieldError,
              handleBlur,
            }) => (
              <>
                <ProfilePicContainer>
                  <ImageWrapper onClick={() => fileRef.current.click()}>
                    <ProfilePic
                      src={
                        values?.avatarUrl
                          ? values.avatarUrl
                          : require("../../../../assets/images/userPlaceholder.svg")
                      }
                    ></ProfilePic>
                    <PickerIcon
                      src={require("../../../../assets/images/imagePicker.svg")}
                    />
                  </ImageWrapper>
                </ProfilePicContainer>
                <BasicDetails>
                  <LabelValueContainer>
                    <Label>{labels["user.name"]}</Label>
                    <Value>{userObject?.name}</Value>
                  </LabelValueContainer>
                  <LabelValueContainer>
                    <Label>{labels["user.email"]}</Label>
                    <Value>{userObject?.email}</Value>
                  </LabelValueContainer>
                  <LabelValueContainer>
                    <Label>{labels["user.mobile"]}</Label>
                    <Value>{userObject?.mobile}</Value>
                  </LabelValueContainer>
                </BasicDetails>
                <Flex style={{ marginBottom: 20 }}>
                  <FormField
                    input={{
                      name: "panNumber",
                      label: labels["user.pan"],
                      type: "text",
                      placeholder: "Eg. ABCDE1234F",
                    }}
                    {...{
                      touched,
                      errors,
                      values,
                      handleChange,
                      handleBlur,
                    }}
                  />
                </Flex>
                <FormField
                  input={{
                    name: "houseNumber",
                    label: labels["user.house_label"],
                    type: "text",
                    placeholder: "Eg. Flat 2C, PKP Apartment",
                    required: true,
                  }}
                  {...{
                    touched,
                    errors,
                    values,
                    handleChange,
                    handleBlur,
                  }}
                />
                <Flex>
                  <HalfWidth>
                    <FormField
                      input={{
                        name: "locality",
                        label: labels["user.locality"],
                        type: "text",
                        placeholder: "Eg. Saket",
                        required: true,
                      }}
                      {...{
                        touched,
                        errors,
                        values,
                        handleChange,
                        handleBlur,
                      }}
                    />
                  </HalfWidth>
                  <HalfWidth>
                    <FormField
                      input={{
                        name: "city",
                        label: labels["user.city"],
                        type: "text",
                        placeholder: "Eg. Delhi",
                        required: true,
                      }}
                      {...{
                        touched,
                        errors,
                        values,
                        handleChange,
                        handleBlur,
                      }}
                    />
                  </HalfWidth>
                </Flex>
                <Flex>
                  <HalfWidth style={{ marginBottom: 10 }}>
                    <FormField
                      input={{
                        name: "state",
                        label: labels["user.state"],
                        type: "select",
                        required: true,
                      }}
                      menuItems={[...states]}
                      {...{
                        touched,
                        errors,
                        values,
                        handleChange,
                        handleBlur,
                      }}
                    />
                  </HalfWidth>
                  <HalfWidth className="disabledArrows">
                    <FormField
                      input={{
                        name: "pin",
                        label: labels["user.pin"],
                        type: "number",
                        placeholder: "Eg. 110016",
                        required: true,
                      }}
                      {...{
                        touched,
                        errors,
                        values,
                        handleChange,
                        handleBlur,
                      }}
                    />
                  </HalfWidth>
                </Flex>
                <HalfWidth>
                  <FormField
                    input={{
                      name: "landmark",
                      label: labels["user.landmark"],
                      type: "text",
                      placeholder: "Eg. Garden of 5 senses",
                      required: true,
                    }}
                    {...{
                      touched,
                      errors,
                      values,
                      handleChange,
                      handleBlur,
                    }}
                  />
                </HalfWidth>
                <ButtonContainer>
                  <PrimaryOutlinedCTAButton
                    onClick={() => setModal(false)}
                    style={{ width: "47%" }}
                  >
                    {labels["buttons.back"]}
                  </PrimaryOutlinedCTAButton>
                  <PrimaryCTAButton
                    onClick={handleSubmit}
                    style={{ width: "47%" }}
                  >
                    {labels["buttons.save"]}
                  </PrimaryCTAButton>
                </ButtonContainer>
                <input
                  ref={fileRef}
                  type="file"
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={(e) =>
                    s3Upload(
                      e.target.files[0],
                      "avatarUrl",
                      setFieldValue,
                      setFieldError,
                    )
                  }
                />
              </>
            )}
          </Formik>
        </AgentFormContainer>
      </DrawerContainer>
    </Drawer>
  );
}

const DrawerContainer = styled.div`
  width: 100vw;
  @media ${theme?.breakpoints?.sm_up} {
    width: 618px;
  }
`;

const HeadingContainer = styled.div`
  padding-top: 35px;
  padding-bottom: 20px;
  padding-left: 15px;
  padding-right: 30px;
  border-bottom: 1px solid ${COLORS.INPUT_BORDER};
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media ${theme?.breakpoints?.sm_up} {
    padding-left: 30px;
    padding-right: 54px;
  }
`;

export const LabelValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  margin-bottom: 20px;
  &:last-child {
    margin-bottom: 0;
  }
  @media ${theme?.breakpoints?.sm_up} {
    width: auto;
    margin-bottom: 0;
  }
`;

const CloseModal = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
  cursor: pointer;
`;

const Heading = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 18px;
  font-weight: 800;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
`;

const AgentFormContainer = styled.div`
  padding-top: 14px;
  padding-left: 15px;
  padding-right: 15px;
  padding-bottom: 26px;
  & form {
    padding-top: 10px;
  }
  @media ${theme?.breakpoints?.sm_up} {
    padding-left: 38px;
    padding-right: 38px;
  }
`;

const Flex = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media ${theme?.breakpoints?.sm_up} {
    flex-direction: row;
  }
`;

const HalfWidth = styled.div`
  width: 100%;
  @media ${theme?.breakpoints?.sm_up} {
    width: 48%;
  }
`;

const PickerIcon = styled.img`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 34px;
  height: 34px;
  object-fit: contain;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 91px;
  height: 91px;
  border-radius: 50%;
  margin-bottom: 2px;
  cursor: pointer;
  user-select: none;
`;

const BasicDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 20px;
  @media ${theme?.breakpoints?.sm_up} {
    width: 425px;
  }
`;
